import { useRef, useState } from 'react';
import { X, FileText, Loader2, ImageIcon, UploadCloud } from 'lucide-react';
import { isRenderableImage } from '@/lib/attachments/attachment-files';

export interface AttachmentDropzoneItem {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  description?: string;
}

type AttachmentDropzoneListProps<T extends AttachmentDropzoneItem> = {
  attachments: T[];
  accept?: string;
  deletingId?: string | null;
  helperText?: string;
  onDelete?: (attachment: T) => void | Promise<void>;
  onDownload?: (attachment: T) => void | Promise<void>;
  onFilesSelected?: (files: FileList) => void | Promise<void>;
  readOnly?: boolean;
  uploading?: boolean;
  multiple?: boolean;
  /**
   * Optional map of attachment id → displayable image URL. When an image
   * attachment has an entry here, its thumbnail is rendered inline so users can
   * see the picture they added (product/job photos) instead of a generic icon.
   */
  previewUrls?: Record<string, string>;
};

const DEFAULT_ACCEPT = '.jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,.mp4,.mov';
const DEFAULT_HELPER_TEXT = 'JPEG, PNG, HEIC, WEBP, PDF, MP4, MOV · Max 50 MB';

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mimeType: string) {
  return mimeType.startsWith('image/');
}

export function AttachmentDropzoneList<T extends AttachmentDropzoneItem>({
  attachments,
  accept = DEFAULT_ACCEPT,
  deletingId = null,
  helperText = DEFAULT_HELPER_TEXT,
  onDelete,
  onDownload,
  onFilesSelected,
  readOnly = false,
  uploading = false,
  multiple = false,
  previewUrls,
}: AttachmentDropzoneListProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  async function handleFiles(files: FileList | null) {
    if (!files?.length || !onFilesSelected) return;
    await onFilesSelected(files);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="space-y-3">
      {!readOnly && (
        <>
          <div
            role="button"
            tabIndex={0}
            onClick={() => !uploading && inputRef.current?.click()}
            onKeyDown={(event) => event.key === 'Enter' && !uploading && inputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              if (event.dataTransfer.files.length) void handleFiles(event.dataTransfer.files);
            }}
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
              dragging
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/30'
            } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
          >
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <UploadCloud className="h-6 w-6" />
            )}
            <p className="text-sm font-medium">
              {uploading ? 'Uploading...' : 'Click or drag a file here'}
            </p>
            <p className="text-xs">{helperText}</p>
          </div>

          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept={accept}
            multiple={multiple}
            onChange={(event) => void handleFiles(event.target.files)}
          />
        </>
      )}

      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment) => {
            // Thumbnail + file name are ONE control: clicking the picture is the
            // first thing anyone tries, so it must open the file instead of
            // sitting inert next to a clickable name.
            const openable = Boolean(onDownload);
            return (
              <div
                key={attachment.id}
                className="flex items-center gap-3 rounded-lg border px-3 py-2 text-sm"
              >
                <button
                  type="button"
                  onClick={() => void onDownload?.(attachment)}
                  disabled={!openable}
                  aria-label={openable ? `Open ${attachment.fileName}` : undefined}
                  className={`flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default ${
                    openable ? 'group cursor-pointer' : ''
                  }`}
                >
                  {isRenderableImage(attachment.mimeType) && previewUrls?.[attachment.id] ? (
                    <img
                      src={previewUrls[attachment.id]}
                      alt={attachment.fileName}
                      loading="lazy"
                      decoding="async"
                      className="h-10 w-10 shrink-0 rounded border object-cover"
                    />
                  ) : isImage(attachment.mimeType) ? (
                    <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium group-hover:underline">
                      {attachment.fileName}
                    </span>
                    {attachment.description && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {attachment.description}
                      </span>
                    )}
                  </span>
                </button>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatBytes(attachment.fileSize)}
                </span>
                {!readOnly && onDelete ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      // Removing a file must never also open it.
                      event.stopPropagation();
                      void onDelete(attachment);
                    }}
                    disabled={deletingId === attachment.id}
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-destructive disabled:opacity-50"
                    aria-label={`Delete ${attachment.fileName}`}
                  >
                    {deletingId === attachment.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <X className="h-3.5 w-3.5" />
                    )}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
